/* ============================================================
   sl-loop-videos.js — loop videos · studiolab
   VERSION: 1.2.3

   Sits on top of sl-loop.js. The carousel still measures slots;
   this file owns focus, playback, sound, and — in manual mode —
   an infinite center-loop so every clip can sit in the middle.

   1.2.3 — First clip no longer seeks/plays before the track has
   landed on it (that was the play-blink on card 0). Poster stays
   up until `playing`. Sound is a per-card icon on the CTA row,
   space-between; only that button unmutes — not a page click
   and not a floating pill.

   1.2.2 — Do not wrap or retarget play on every scroll event.
   wrapIfNeeded + goTo(instant) on scroll fed itself: each jump
   fired scroll again, restarted every <video>, and the strip
   ran away. Wrap only after a real drag; arrows still own the
   index. Same-index setFocused does not reset currentTime.

   1.2.1 — Arrows step one card at a time in block order. Play
   follows that index, not the geometric hot zone (clones at
   scroll 0 made the hot zone pick the third clip). Drag still
   uses the hot zone on gesture-end.

   1.2.0 — The middle card is always the one that plays. Arrows
   are a focus control: they slide the next/previous clip into
   the centre and that clip autoplays. Starts on the first card.
   The strip never ends — a cloned set on each side wraps the
   scroll, so every video gets a turn. Cycle-in-place (1.1.0)
   is gone: painting is-focused without moving the track left
   videos stranded at the edges.

   Sound: browsers refuse unmuted autoplay. We start muted. The
   visitor turns sound on or off with the speaker icon on the
   card (same row as Comprar). iOS ignores video.volume — mute
   toggles there, fade on desktop.

   Idempotent: safe on shopify:section:load.
   ============================================================ */
(function () {
  'use strict';

  var SECTION = '[data-sl-loop-videos]';
  var CARD = '[data-sl-video-card]';
  var VIDEO = '[data-sl-loop-video]';
  var SOUND_BTN = '[data-sl-sound-toggle]';
  var HYSTERESIS = 0.22;
  var IO_THRESHOLD = 0.18;
  var EDITOR_ATTRS = [
    'data-shopify-editor-block',
    'data-shopify-editor-block-id',
    'data-shopify-block-id'
  ];

  function inEditor() {
    return !!(window.Shopify && Shopify.designMode);
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function parseFade(root) {
    var ms = parseInt(root.getAttribute('data-sl-fade'), 10);
    if (!ms || ms < 0) return 400;
    return ms;
  }

  function volumeWritable(video) {
    if (!video) return false;
    try {
      var prev = video.volume;
      video.volume = prev === 1 ? 0.97 : Math.min(1, prev + 0.03);
      var ok = video.volume !== prev;
      video.volume = prev;
      return ok;
    } catch (e) {
      return false;
    }
  }

  function fadeVolume(video, target, ms, done) {
    if (!video) {
      if (done) done();
      return;
    }
    target = Math.max(0, Math.min(1, target));
    if (!volumeWritable(video) || ms <= 0 || prefersReducedMotion()) {
      video.volume = target;
      if (done) done();
      return;
    }
    var from = video.volume;
    if (Math.abs(from - target) < 0.02) {
      video.volume = target;
      if (done) done();
      return;
    }
    if (video._slFadeRaf) cancelAnimationFrame(video._slFadeRaf);
    var t0 = performance.now();
    function frame(now) {
      var t = Math.min(1, (now - t0) / ms);
      var eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      video.volume = from + (target - from) * eased;
      if (t < 1) {
        video._slFadeRaf = requestAnimationFrame(frame);
      } else {
        video._slFadeRaf = null;
        video.volume = target;
        if (done) done();
      }
    }
    video._slFadeRaf = requestAnimationFrame(frame);
  }

  function playSafe(video) {
    var p = video.play();
    if (p && typeof p.catch === 'function') return p.catch(function () {});
    return Promise.resolve();
  }

  function pauseAll(root) {
    var videos = root.querySelectorAll(VIDEO);
    for (var i = 0; i < videos.length; i++) {
      videos[i].pause();
    }
  }

  function getTrack(viewport) {
    return viewport.querySelector('[data-sl-track]');
  }

  function getGap(track) {
    if (!track) return 0;
    var cs = getComputedStyle(track);
    return parseFloat(cs.columnGap) || parseFloat(cs.gap) || 0;
  }

  function originalCards(viewport) {
    var track = getTrack(viewport);
    if (!track) return [];
    var out = [];
    for (var i = 0; i < track.children.length; i++) {
      var node = track.children[i];
      if (node.hasAttribute('data-sl-video-card') && !node.hasAttribute('data-sl-clone')) {
        out.push(node);
      }
    }
    return out;
  }

  function cardIndex(card) {
    if (!card) return -1;
    var raw = card.getAttribute('data-sl-video-index');
    if (raw == null || raw === '') return -1;
    var n = parseInt(raw, 10);
    return isNaN(n) ? -1 : n;
  }

  /* 0-based position among originals. data-sl-video-index can be
     1-based if a Liquid render param swallowed `| minus: 1`; goTo
     must never treat that as an array index or each sync steps
     the strip forward on its own. */
  function originalPosition(viewport, card) {
    var originals = originalCards(viewport);
    var idx = cardIndex(card);
    for (var i = 0; i < originals.length; i++) {
      if (originals[i] === card || cardIndex(originals[i]) === idx) return i;
    }
    return -1;
  }

  function slotWidth(viewport, fallbackRect) {
    var root = viewport.closest('[id^="sl-section-"]');
    var fromVar = root ? parseFloat(getComputedStyle(root).getPropertyValue('--sl-slot')) : 0;
    if (fromVar > 0) return fromVar;
    return fallbackRect && fallbackRect.width ? fallbackRect.width : viewport.clientWidth / 3;
  }

  function centerScroll(viewport, card) {
    if (!card) return 0;
    var slot = card.getBoundingClientRect().width;
    return card.offsetLeft - (viewport.clientWidth - slot) / 2;
  }

  function instantScroll(state, left) {
    var viewport = state.viewport;
    state.programmatic = true;
    viewport.classList.add('is-jumping');
    viewport.scrollLeft = left;
    if (state.jumpTimer) clearTimeout(state.jumpTimer);
    state.jumpTimer = setTimeout(function () {
      viewport.classList.remove('is-jumping');
      state.programmatic = false;
      state.jumpTimer = 0;
    }, 48);
  }

  function alreadyCentered(state, card) {
    if (!card) return false;
    return Math.abs(state.viewport.scrollLeft - centerScroll(state.viewport, card)) < 4;
  }

  function waitScrollEnd(viewport, done) {
    var finished = false;
    var idle = 0;
    var fallback = 0;
    function finish() {
      if (finished) return;
      finished = true;
      viewport.removeEventListener('scrollend', finish);
      viewport.removeEventListener('scroll', onScroll);
      clearTimeout(idle);
      clearTimeout(fallback);
      done();
    }
    function onScroll() {
      clearTimeout(idle);
      idle = setTimeout(finish, 140);
    }
    fallback = setTimeout(finish, 800);
    viewport.addEventListener('scrollend', finish);
    viewport.addEventListener('scroll', onScroll);
  }

  /* Occupant of the middle slot. Two cards on the seam tie on
     overlap — pick the left so play does not flicker. */
  function pickFocused(viewport, current) {
    var cards = viewport.querySelectorAll(CARD);
    if (!cards.length) return null;
    var root = viewport.getBoundingClientRect();
    var slot = slotWidth(viewport, cards[0].getBoundingClientRect());
    var zoneLeft = root.left + (root.width - slot) / 2;
    var zoneRight = zoneLeft + slot;
    var best = null;
    var bestOverlap = 0;

    for (var i = 0; i < cards.length; i++) {
      var r = cards[i].getBoundingClientRect();
      if (r.right < root.left || r.left > root.right) continue;
      var overlap = Math.min(r.right, zoneRight) - Math.max(r.left, zoneLeft);
      if (overlap <= 0) continue;
      if (overlap > bestOverlap + 0.5) {
        bestOverlap = overlap;
        best = cards[i];
      } else if (best && Math.abs(overlap - bestOverlap) <= 0.5 && r.left < best.getBoundingClientRect().left) {
        best = cards[i];
      }
    }

    if (!best) return current || null;
    if (!current || current === best || !current.isConnected) return best;

    var curR = current.getBoundingClientRect();
    var curOverlap = Math.min(curR.right, zoneRight) - Math.max(curR.left, zoneLeft);
    if (curOverlap > 0 && bestOverlap - curOverlap < slot * HYSTERESIS) return current;
    return best;
  }

  function soundAllowed(state) {
    if (!state.soundEnabled || state.userMuted || !state.inView || inEditor()) return false;
    return state.soundUnlocked;
  }

  function applySound(video, state, fadeOut, done) {
    if (!video) {
      if (done) done();
      return;
    }
    var allow = soundAllowed(state);
    if (!allow) {
      fadeVolume(video, 0, fadeOut ? state.fadeMs : 0, function () {
        video.muted = true;
        video.volume = 0;
        if (done) done();
      });
      return;
    }
    video.muted = false;
    state.soundUnlocked = true;
    fadeVolume(video, 1, state.fadeMs, done);
  }

  function armVideos(root) {
    var videos = root.querySelectorAll(VIDEO);
    for (var i = 0; i < videos.length; i++) {
      var video = videos[i];
      if (video._slArmed) continue;
      video._slArmed = true;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.volume = 0;
      video.addEventListener('playing', function () {
        this.classList.add('is-ready');
      });
    }
  }

  function endBoot(state) {
    if (!state.root.classList.contains('is-booting')) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        state.root.classList.remove('is-booting');
      });
    });
  }

  function paintFocusClasses(state, card) {
    var cards = state.root.querySelectorAll(CARD);
    var idx = cardIndex(card);
    for (var i = 0; i < cards.length; i++) {
      var match = idx >= 0 ? cardIndex(cards[i]) === idx : cards[i] === card;
      cards[i].classList.toggle('is-focused', match);
    }
  }

  function setFocused(state, card) {
    var videos = state.root.querySelectorAll(VIDEO);
    var focusedVideo = card ? card.querySelector(VIDEO) : null;
    var idx = cardIndex(card);
    var prev = state.focused;
    var prevVideo = prev && prev.querySelector(VIDEO);
    var sameIndex = idx >= 0 && prev && cardIndex(prev) === idx;

    paintFocusClasses(state, card);

    if (!state.inView) {
      pauseAll(state.root);
      state.focused = card;
      var parked = originalPosition(state.viewport, card);
      if (parked >= 0) {
        state.logicalIndex = parked;
        setDots(state, parked);
      }
      updateSoundButtons(state);
      return;
    }

    if (sameIndex && focusedVideo && prevVideo === focusedVideo && !focusedVideo.paused) {
      state.focused = card;
      var pos = originalPosition(state.viewport, card);
      if (pos >= 0) {
        state.logicalIndex = pos;
        setDots(state, pos);
      }
      updateSoundButtons(state);
      return;
    }

    for (var j = 0; j < videos.length; j++) {
      var video = videos[j];
      var on = video === focusedVideo;
      if (on) {
        if (video.paused) {
          if (sameIndex && prevVideo && prevVideo !== video) {
            try { video.currentTime = prevVideo.currentTime; } catch (e) {}
          } else if (!sameIndex && video.currentTime > 0.08) {
            video.classList.remove('is-ready');
            try { video.currentTime = 0; } catch (e) {}
          }
        }
        video.playsInline = true;
        video.muted = !soundAllowed(state);
        playSafe(video);
        applySound(video, state, false);
      } else {
        if (!video.paused) video.pause();
        video.volume = 0;
        video.muted = true;
      }
    }

    state.focused = card;
    var nextPos = originalPosition(state.viewport, card);
    if (nextPos >= 0) {
      state.logicalIndex = nextPos;
      setDots(state, nextPos);
    }
    updateSoundButtons(state);
  }

  function updateSoundButtons(state) {
    var buttons = state.root.querySelectorAll(SOUND_BTN);
    var on = !!(state.soundEnabled && state.soundUnlocked && !state.userMuted && !inEditor());
    for (var i = 0; i < buttons.length; i++) {
      if (!state.soundEnabled || inEditor()) {
        buttons[i].hidden = true;
        continue;
      }
      buttons[i].hidden = false;
      buttons[i].classList.toggle('is-on', on);
      buttons[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      buttons[i].setAttribute('aria-label', on ? 'Desativar som' : 'Ativar som');
    }
  }

  function setDots(state, index) {
    var dots = state.root.querySelectorAll('[data-sl-dot-index]');
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('is-active', i === index);
    }
  }

  function unlockArrows(state) {
    var carousel = state.viewport.parentElement;
    if (!carousel) return;
    var prev = carousel.querySelector('[data-sl-prev]');
    var next = carousel.querySelector('[data-sl-next]');
    if (prev) prev.disabled = false;
    if (next) next.disabled = false;
  }

  function stripEditorAttrs(node) {
    EDITOR_ATTRS.forEach(function (attr) {
      if (node.hasAttribute && node.hasAttribute(attr)) node.removeAttribute(attr);
    });
    var nested = node.querySelectorAll ? node.querySelectorAll('*') : [];
    for (var i = 0; i < nested.length; i++) {
      EDITOR_ATTRS.forEach(function (attr) {
        if (nested[i].hasAttribute(attr)) nested[i].removeAttribute(attr);
      });
    }
  }

  function makeClone(card) {
    var clone = card.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-sl-clone', '');
    stripEditorAttrs(clone);
    var links = clone.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) links[i].setAttribute('tabindex', '-1');
    var buttons = clone.querySelectorAll('button');
    for (var b = 0; b < buttons.length; b++) buttons[b].setAttribute('tabindex', '-1');
    var video = clone.querySelector(VIDEO);
    if (video) {
      video.muted = true;
      video.defaultMuted = true;
      video.volume = 0;
      video.classList.remove('is-ready');
      video._slArmed = false;
      try { video.pause(); } catch (e) {}
    }
    return clone;
  }

  function removeLoopClones(track) {
    var clones = track.querySelectorAll('[data-sl-clone]');
    for (var i = clones.length - 1; i >= 0; i--) {
      clones[i].parentNode.removeChild(clones[i]);
    }
  }

  function loopClonesHealthy(state) {
    var track = getTrack(state.viewport);
    if (!track || !state.setWidth) return false;
    var originals = originalCards(state.viewport);
    if (originals.length < 2) return false;
    return track.querySelectorAll('[data-sl-clone]').length === originals.length * 2;
  }

  function buildCenterLoop(state) {
    var viewport = state.viewport;
    if (viewport.dataset.mode !== 'manual') {
      state.loopReady = false;
      state.root.classList.remove('is-center-loop');
      return;
    }

    var track = getTrack(viewport);
    if (!track) return;

    if (viewport.clientWidth < 8) return;

    if (!state.rebuildLoop && loopClonesHealthy(state)) {
      state.loopReady = true;
      state.root.classList.add('is-center-loop');
      unlockArrows(state);
      return;
    }
    state.rebuildLoop = false;

    var originals = originalCards(viewport);
    for (var p = 0; p < originals.length; p++) {
      var playing = originals[p].querySelector(VIDEO);
      if (playing) {
        try { playing.pause(); } catch (e) {}
      }
    }

    removeLoopClones(track);
    var originals = originalCards(viewport);
    if (originals.length < 2) {
      state.loopReady = false;
      state.root.classList.remove('is-center-loop');
      return;
    }

    var first = originals[0];
    var frag = document.createDocumentFragment();
    for (var i = 0; i < originals.length; i++) {
      frag.appendChild(makeClone(originals[i]));
    }
    track.insertBefore(frag, first);

    for (var j = 0; j < originals.length; j++) {
      track.appendChild(makeClone(originals[j]));
    }

    originals = originalCards(viewport);
    var appended = originals[0].nextElementSibling;
    while (appended && !appended.hasAttribute('data-sl-clone')) {
      appended = appended.nextElementSibling;
    }
    /* Distance from the first original to the matching appended clone. */
    var setWidth = 0;
    if (appended) {
      setWidth = appended.offsetLeft - originals[0].offsetLeft;
    }
    if (setWidth <= 0) {
      var gap = getGap(track);
      for (var k = 0; k < originals.length; k++) {
        setWidth += originals[k].getBoundingClientRect().width + gap;
      }
    }

    state.setWidth = setWidth;
    state.loopReady = true;
    state.root.classList.add('is-center-loop');
    armVideos(state.root);
    unlockArrows(state);
  }

  function cardByIndex(state, index, where) {
    var originals = originalCards(state.viewport);
    var n = originals.length;
    if (!n) return null;
    index = ((index % n) + n) % n;
    if (where === 'original' || !where) return originals[index];

    var track = getTrack(state.viewport);
    if (!track) return originals[index];
    var clones = [];
    for (var i = 0; i < track.children.length; i++) {
      var node = track.children[i];
      if (node.hasAttribute('data-sl-clone') && cardIndex(node) === index) {
        clones.push(node);
      }
    }
    if (!clones.length) return originals[index];
    if (where === 'before') return clones[0];
    return clones[clones.length - 1];
  }

  function goTo(state, index, instant) {
    if (!state.loopReady) return;
    var originals = originalCards(state.viewport);
    var n = originals.length;
    if (!n) return;

    if (state.lockWrap && !instant) {
      state.lockWrap = false;
      instantScroll(state, centerScroll(state.viewport, originals[state.logicalIndex || 0]));
    }

    var from = state.logicalIndex == null ? 0 : state.logicalIndex;
    var wrapped = ((index % n) + n) % n;
    var target = originals[wrapped];
    var jumpingClone = false;

    if (!instant && n > 1) {
      if (from === n - 1 && wrapped === 0) {
        target = cardByIndex(state, 0, 'after') || target;
        jumpingClone = target !== originals[0];
      } else if (from === 0 && wrapped === n - 1) {
        target = cardByIndex(state, n - 1, 'before') || target;
        jumpingClone = target !== originals[n - 1];
      }
    }

    if (!jumpingClone && alreadyCentered(state, originals[wrapped]) && state.focused && originalPosition(state.viewport, state.focused) === wrapped) {
      setFocused(state, originals[wrapped]);
      unlockArrows(state);
      endBoot(state);
      return;
    }

    state.navGen = (state.navGen || 0) + 1;
    var gen = state.navGen;
    state.logicalIndex = wrapped;
    unlockArrows(state);
    setDots(state, wrapped);

    var left = centerScroll(state.viewport, target);
    if (instant || prefersReducedMotion()) {
      instantScroll(state, left);
      state.lockWrap = false;
      if (jumpingClone) {
        instantScroll(state, centerScroll(state.viewport, originals[wrapped]));
        setFocused(state, originals[wrapped]);
      } else {
        setFocused(state, target);
      }
      endBoot(state);
      return;
    }

    paintFocusClasses(state, target);
    state.lockWrap = true;
    state.viewport.classList.add('is-jumping');
    state.viewport.scrollTo({ left: left, behavior: 'smooth' });
    waitScrollEnd(state.viewport, function () {
      if (gen !== state.navGen) return;
      if (jumpingClone) {
        instantScroll(state, centerScroll(state.viewport, originals[wrapped]));
        setFocused(state, originals[wrapped]);
      } else {
        setFocused(state, target);
      }
      state.viewport.classList.remove('is-jumping');
      state.lockWrap = false;
      unlockArrows(state);
    });
  }

  function wrapIfNeeded(state) {
    if (!state.loopReady || state.lockWrap || state.wrapping || !state.setWidth) return;
    var originals = originalCards(state.viewport);
    if (originals.length < 2) return;
    var first = centerScroll(state.viewport, originals[0]);
    var last = centerScroll(state.viewport, originals[originals.length - 1]);
    var sl = state.viewport.scrollLeft;
    state.wrapping = true;
    if (sl < first - 2) {
      instantScroll(state, sl + state.setWidth);
    } else if (sl > last + 2) {
      instantScroll(state, sl - state.setWidth);
    }
    state.wrapping = false;
  }

  function settleAfterUserScroll(state) {
    if (!state.loopReady || state.lockWrap || state.programmatic) return;
    wrapIfNeeded(state);
    var card = pickFocused(state.viewport, state.focused);
    var pos = originalPosition(state.viewport, card);
    if (pos < 0) return;
    goTo(state, pos, true);
  }

  function patchControls(state) {
    var vp = state.viewport;
    var n = 0;
    function wrap() {
      if (vp._slVideosPatched) return;
      var original = vp.slUpdateControls;
      if (typeof original === 'function') {
        vp._slVideosPatched = true;
        vp.slUpdateControls = function () {
          original.call(vp);
          if (state.loopReady) unlockArrows(state);
        };
        return;
      }
      if (n++ < 12) requestAnimationFrame(wrap);
    }
    wrap();
  }

  function tick(state) {
    if (!state.inView) return;
    /* Manual center-loop: play order is the arrow index, not the
       hot zone. Clones at scroll 0 made the zone pick clip 3 of 5. */
    if (state.loopReady) {
      unlockArrows(state);
      return;
    }
    var next = pickFocused(state.viewport, state.focused);
    if (next !== state.focused) setFocused(state, next);
    state.raf = requestAnimationFrame(function () { tick(state); });
  }

  function startTick(state) {
    if (state.raf) return;
    state.raf = requestAnimationFrame(function () { tick(state); });
  }

  function stopTick(state) {
    if (state.raf) {
      cancelAnimationFrame(state.raf);
      state.raf = 0;
    }
  }

  function syncLoop(state, forceGo) {
    if (state.viewport.dataset.mode !== 'manual') {
      buildCenterLoop(state);
      return;
    }
    var width = state.viewport.clientWidth;
    var sameWidth = state.lastWidth > 0 && Math.abs(width - state.lastWidth) <= 2 && state.loopReady && loopClonesHealthy(state);
    if (sameWidth) {
      unlockArrows(state);
      if (forceGo) goTo(state, state.logicalIndex == null ? 0 : state.logicalIndex, true);
      return;
    }
    if (width && state.lastWidth && Math.abs(width - state.lastWidth) > 2) {
      state.rebuildLoop = true;
    }
    var keep = state.logicalIndex == null ? 0 : state.logicalIndex;
    buildCenterLoop(state);
    state.lastWidth = width;
    if (state.loopReady) goTo(state, keep, true);
  }

  function enterView(state) {
    state.inView = true;
    syncLoop(state, !state.loopReady);
    if (state.loopReady) {
      goTo(state, state.logicalIndex || 0, true);
    } else {
      startTick(state);
      var next = pickFocused(state.viewport, state.focused);
      setFocused(state, next);
    }
    updateSoundButtons(state);
  }

  function leaveView(state) {
    state.inView = false;
    stopTick(state);
    var focusedVideo = state.focused && state.focused.querySelector(VIDEO);
    applySound(focusedVideo, state, true, function () {
      pauseAll(state.root);
    });
    updateSoundButtons(state);
  }

  function bind(root) {
    if (root.dataset.slLoopVideosBound === 'true') return;
    root.dataset.slLoopVideosBound = 'true';

    var viewport = root.querySelector('[data-sl-carousel]');
    if (!viewport) return;

    var state = {
      root: root,
      viewport: viewport,
      soundEnabled: root.getAttribute('data-sl-sound') === 'true',
      fadeMs: parseFade(root),
      soundUnlocked: false,
      userMuted: false,
      inView: false,
      focused: null,
      logicalIndex: 0,
      loopReady: false,
      lockWrap: false,
      wrapping: false,
      programmatic: false,
      rebuildLoop: false,
      dragStartScroll: 0,
      dragging: false,
      setWidth: 0,
      lastWidth: 0,
      raf: 0
    };

    root._slLoopVideosState = state;
    if (inEditor()) state.soundEnabled = false;

    armVideos(root);

    root.addEventListener('pointerdown', function (e) {
      if (!(e.target.closest && e.target.closest(SOUND_BTN))) return;
      e.stopPropagation();
    }, true);

    root.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest(SOUND_BTN);
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      if (!state.soundEnabled || inEditor()) return;
      if (state.soundUnlocked && !state.userMuted) {
        state.userMuted = true;
      } else {
        state.userMuted = false;
        state.soundUnlocked = true;
      }
      var video = state.focused && state.focused.querySelector(VIDEO);
      applySound(video, state, state.userMuted);
      updateSoundButtons(state);
    }, true);

    var carousel = viewport.parentElement;
    if (carousel) {
      carousel.addEventListener('click', function (e) {
        if (viewport.dataset.mode !== 'manual') return;
        var isNext = e.target.closest && e.target.closest('[data-sl-next]');
        var isPrev = e.target.closest && e.target.closest('[data-sl-prev]');
        var dot = e.target.closest && e.target.closest('[data-sl-dot-index]');
        if (!isNext && !isPrev && !dot) return;
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        if (!state.loopReady) syncLoop(state, true);
        if (isNext) goTo(state, (state.logicalIndex || 0) + 1, false);
        else if (isPrev) goTo(state, (state.logicalIndex || 0) - 1, false);
        else goTo(state, parseInt(dot.getAttribute('data-sl-dot-index'), 10) || 0, false);
      }, true);
    }

    viewport.addEventListener('click', function (e) {
      if (viewport.dataset.mode !== 'manual' || !state.loopReady) return;
      if (e.target.closest && e.target.closest('a')) return;
      if (e.target.closest && e.target.closest(SOUND_BTN)) return;
      var card = e.target.closest && e.target.closest(CARD);
      if (!card) return;
      var pos = originalPosition(state.viewport, card);
      if (pos < 0) return;
      goTo(state, pos, false);
    });

    patchControls(state);

    viewport.addEventListener('wheel', function (e) {
      if (!state.loopReady) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey) e.preventDefault();
    }, { passive: false });

    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      state.dragging = true;
      state.dragStartScroll = viewport.scrollLeft;
    });
    function onPointerRelease() {
      if (!state.dragging) return;
      state.dragging = false;
      if (Math.abs(viewport.scrollLeft - state.dragStartScroll) < 8) return;
      requestAnimationFrame(function () { settleAfterUserScroll(state); });
    }
    viewport.addEventListener('pointerup', onPointerRelease);
    viewport.addEventListener('pointercancel', onPointerRelease);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        syncLoop(state, true);
      });
    });

    if (typeof ResizeObserver !== 'undefined') {
      var fitRo = new ResizeObserver(function () {
        syncLoop(state, false);
      });
      fitRo.observe(viewport);
    } else {
      window.addEventListener('resize', function () { syncLoop(state, false); });
    }

    if (!('IntersectionObserver' in window)) {
      enterView(state);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) enterView(state);
          else leaveView(state);
        });
      },
      { root: null, threshold: IO_THRESHOLD }
    );
    io.observe(root);
    updateSoundButtons(state);
  }

  function scan(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var nodes = root.querySelectorAll(SECTION);
    for (var i = 0; i < nodes.length; i++) bind(nodes[i]);
    if (root.matches && root.matches(SECTION)) bind(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }

  window.addEventListener('load', function () {
    var nodes = document.querySelectorAll(SECTION);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i]._slLoopVideosState) syncLoop(nodes[i]._slLoopVideosState, false);
    }
  });

  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
  });
})();
